import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import StarRating from "@/components/base/StarRating";
import useSeo from "@/hooks/useSeo";
import { synopsis, characters } from "@/mocks/movie";
import { reviews } from "@/mocks/reviews";
import { news } from "@/mocks/news";

type ResultType = "全部" | "剧情" | "影评" | "资讯";
const typeFilters: ResultType[] = ["全部", "剧情", "影评", "资讯"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const keyword = q.trim().toLowerCase();

  const [input, setInput] = useState(q);
  const [type, setType] = useState<ResultType>("全部");
  const [order, setOrder] = useState("relevance");

  useEffect(() => {
    setInput(q);
  }, [q]);

  useSeo({
    title: keyword ? `“${q}” 的搜索结果 - 激情丛林电影` : "站内搜索 - 激情丛林电影",
    description:
      "在激情丛林电影站点内搜索剧情介绍、影评与资讯内容，快速定位你关心的影片细节与讨论。",
    keywords: "激情丛林搜索,电影搜索,剧情搜索,影评搜索,资讯搜索",
  });

  const matchedReviews = useMemo(() => {
    if (!keyword) return [];
    const list = reviews.filter((r) =>
      [r.title, r.author, r.source, r.excerpt, r.category, r.tags.join(" "), r.content.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );
    return [...list].sort((a, b) =>
      order === "date" ? (a.date < b.date ? 1 : -1) : b.rating - a.rating
    );
  }, [keyword, order]);

  const matchedNews = useMemo(() => {
    if (!keyword) return [];
    const list = news.filter((n) =>
      [n.title, n.category, n.excerpt, n.content.join(" ")].join(" ").toLowerCase().includes(keyword)
    );
    return [...list].sort((a, b) =>
      order === "date" ? (a.date < b.date ? 1 : -1) : a.date < b.date ? 1 : -1
    );
  }, [keyword, order]);

  const plotMatched = useMemo(() => {
    if (!keyword) return false;
    const inSynopsis = synopsis.some((p) => p.toLowerCase().includes(keyword));
    const inChars = characters.some((c) =>
      `${c.name}${c.actor}${c.role}${c.bio}${c.traits.join("")}`.toLowerCase().includes(keyword)
    );
    return inSynopsis || inChars;
  }, [keyword]);

  const showPlot = plotMatched && (type === "全部" || type === "剧情");
  const showReviews = matchedReviews.length > 0 && (type === "全部" || type === "影评");
  const showNews = matchedNews.length > 0 && (type === "全部" || type === "资讯");

  const totalCount =
    (showPlot ? 1 : 0) + (showReviews ? matchedReviews.length : 0) + (showNews ? matchedNews.length : 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = input.trim();
    if (v) setSearchParams({ q: v });
    else setSearchParams({});
    setType("全部");
  };

  return (
    <div className="w-full">
      <PageHero
        title="站内搜索"
        subtitle="输入关键词，同时检索剧情介绍、影评与资讯内容。"
        crumbs={[{ label: "搜索" }]}
      >
        <form
          onSubmit={submit}
          className="flex w-full max-w-2xl flex-col gap-3 sm:flex-row"
        >
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-700 bg-background-900/80 px-4 py-3 transition-colors focus-within:border-primary-500">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="例如：林深、雨林、配乐、路演"
              className="w-full bg-transparent font-label text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none"
              aria-label="搜索关键词"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 font-label text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </form>
      </PageHero>

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        {!keyword ? (
          <div className="panel flex flex-col items-center justify-center rounded-lg px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-400">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-50">
              输入关键词开始搜索
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-400">
              试试这些关键词：林深、绿色迷宫、配乐、路演、密钥延期。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {["林深", "雨林", "影评", "路演", "配乐"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setInput(k);
                    setSearchParams({ q: k });
                  }}
                  className="rounded-full border border-background-700 px-4 py-1.5 font-label text-sm text-foreground-300 transition-colors hover:border-primary-500 hover:text-accent-400"
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-col gap-4 border-b border-background-800 pb-5 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-label text-sm text-foreground-400">
                  关键词：
                  <span className="ml-1 font-semibold text-accent-400">“{q}”</span>
                </p>
                <p className="mt-1 font-label text-xs text-foreground-500">
                  共找到 {totalCount} 条相关结果
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {typeFilters.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                        type === t
                          ? "bg-primary-500 font-semibold text-background-50"
                          : "border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-accent-400"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <select
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  aria-label="结果排序"
                  className="cursor-pointer rounded-md border border-background-700 bg-background-900 px-3 py-2 font-label text-sm text-foreground-100 focus:border-primary-500 focus:outline-none"
                >
                  <option value="relevance">相关度 / 评分优先</option>
                  <option value="date">按发布日期</option>
                </select>
              </div>
            </div>

            {totalCount === 0 ? (
              <div className="panel flex flex-col items-center justify-center rounded-lg px-6 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-400">
                  <i className="ri-emotion-unhappy-line text-3xl"></i>
                </span>
                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-50">
                  没有找到相关内容
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-400">
                  换个关键词试试，或者直接浏览以下栏目。
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/plot"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-4 py-2.5 font-label text-sm text-foreground-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                  >
                    <i className="ri-book-open-line"></i>
                    剧情介绍
                  </Link>
                  <Link
                    to="/reviews"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-4 py-2.5 font-label text-sm text-foreground-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                  >
                    <i className="ri-chat-quote-line"></i>
                    影评中心
                  </Link>
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-4 py-2.5 font-label text-sm text-foreground-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                  >
                    <i className="ri-newspaper-line"></i>
                    资讯动态
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-12">
                {showPlot && (
                  <div>
                    <h2 className="mb-4 flex items-center gap-3 font-heading text-lg font-bold text-foreground-50">
                      <i className="ri-book-open-line text-accent-500"></i>
                      剧情介绍
                      <span className="font-label text-xs font-normal text-foreground-500">1 条结果</span>
                    </h2>
                    <Link
                      to="/plot"
                      className="panel group block rounded-lg p-5 transition-colors hover:border-primary-500"
                    >
                      <h3 className="font-heading text-base font-bold text-foreground-50 group-hover:text-accent-400">
                        电影《激情丛林》剧情梗概与角色介绍
                      </h3>
                      <p className="mt-2 line-clamp-2-custom text-sm text-foreground-400">
                        关键词“{q}”出现在剧情介绍或角色设定中，点击查看完整剧情解析与主要角色详情。
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1.5 font-label text-sm text-accent-500">
                        查看剧情介绍
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </Link>
                  </div>
                )}

                {showReviews && (
                  <div>
                    <h2 className="mb-4 flex items-center gap-3 font-heading text-lg font-bold text-foreground-50">
                      <i className="ri-chat-quote-line text-accent-500"></i>
                      影评
                      <span className="font-label text-xs font-normal text-foreground-500">
                        {matchedReviews.length} 条结果
                      </span>
                    </h2>
                    <div className="flex flex-col gap-4">
                      {matchedReviews.map((r) => (
                        <Link
                          key={r.id}
                          to={`/review/${r.id}`}
                          className="panel group flex flex-col gap-4 rounded-lg p-4 transition-colors hover:border-primary-500 sm:flex-row sm:items-center"
                        >
                          <span className="h-32 w-full shrink-0 overflow-hidden rounded-md sm:h-20 sm:w-32">
                            <img
                              src={r.cover}
                              alt={`${r.title} 配图`}
                              title={`${r.title} - 激情丛林电影影评`}
                              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-heading text-base font-bold text-foreground-50 group-hover:text-accent-400">
                              {r.title}
                            </span>
                            <span className="mt-1.5 block line-clamp-2-custom text-sm text-foreground-400">
                              {r.excerpt}
                            </span>
                            <span className="mt-2 flex items-center gap-4">
                              <StarRating rating={r.rating} showValue />
                              <span className="font-label text-xs text-foreground-500">
                                {r.author} · {r.date}
                              </span>
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {showNews && (
                  <div>
                    <h2 className="mb-4 flex items-center gap-3 font-heading text-lg font-bold text-foreground-50">
                      <i className="ri-newspaper-line text-accent-500"></i>
                      资讯
                      <span className="font-label text-xs font-normal text-foreground-500">
                        {matchedNews.length} 条结果
                      </span>
                    </h2>
                    <div className="flex flex-col gap-4">
                      {matchedNews.map((n) => (
                        <Link
                          key={n.id}
                          to={`/news/${n.id}`}
                          className="panel group flex flex-col gap-4 rounded-lg p-4 transition-colors hover:border-primary-500 sm:flex-row sm:items-center"
                        >
                          <span className="h-32 w-full shrink-0 overflow-hidden rounded-md sm:h-20 sm:w-32">
                            <img
                              src={n.cover}
                              alt={`${n.title} 配图`}
                              title={`${n.title} - 激情丛林电影资讯`}
                              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-3">
                              <span className="rounded-full bg-secondary-100/10 px-2.5 py-0.5 font-label text-[11px] text-secondary-300">
                                {n.category}
                              </span>
                              <span className="font-label text-xs text-foreground-500">{n.date}</span>
                            </span>
                            <span className="mt-1.5 block font-heading text-base font-bold text-foreground-50 group-hover:text-accent-400">
                              {n.title}
                            </span>
                            <span className="mt-1 block line-clamp-2-custom text-sm text-foreground-400">
                              {n.excerpt}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}